/* DealsPage.jsx — Premium operational Transactions list.
 *
 * Refined to the calm executive aesthetic (Feb 2026):
 *   1. Header — calm title (no eyebrow) + subtitle on the left;
 *      Search · Filters · Create deal (dark slate) on the right rail.
 *   2. View tabs — underline style (Transactions · Properties ·
 *      Viewings · Progression) with a violet active line.
 *   3. 5-card KPI strip — soft icon container, micro uppercase
 *      label, large semibold number, calm 30d delta footer.
 *   4. Filter row — `All` dark slate pill + plain-text status
 *      buttons; Filters icon button right-aligned.
 *   5. Premium row table — property image + address + type;
 *      parties as avatar lines with calm +N overflow; stage with
 *      progress bar + `X / N`; outlined status/risk pills; Value
 *      bold slate; Updated rolled into Status sub-line.
 *
 * Sidebar untouched.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus, Search, SlidersHorizontal, ArrowRight, ArrowUp, ArrowDown,
  TrendingUp, AlertTriangle, ShieldCheck, CheckCircle2, XCircle,
  ChevronLeft, ChevronRight, MoreHorizontal,
} from "lucide-react";
import { Sidebar } from "../components/Sidebar";
import { useChainStore, openCreateDeal, openAddProperty } from "../data/chainStore";
import { ChainRibbon } from "../components/operational/ChainRibbon";
import { pickAnchorDeal } from "../lib/chainRelationships";
import { useSampleData } from "../hooks/useSampleData";
import PropertiesPage from "./PropertiesPage";
import ViewingsPage from "./ViewingsPage";
import SalesProgressionPage from "./SalesProgressionPage";

// ─── Tones ────────────────────────────────────────────────────────

const STATUS_TONE = {
  Active:           { text: "#047857", border: "#A7F3D0", bg: "#ECFDF5" },
  "At Risk":        { text: "#B45309", border: "#FDE68A", bg: "#FFFBEB" },
  Exchanged:        { text: "#5B21B6", border: "#DDD6FE", bg: "#F5F3FF" },
  Completed:        { text: "#065F46", border: "#86EFAC", bg: "#ECFDF5" },
  "Fallen Through": { text: "#9F1239", border: "#FECDD3", bg: "#FFF1F2" },
};

const RISK_TONE = {
  Low:    { text: "#047857", border: "#A7F3D0", bg: "#ECFDF5" },
  Medium: { text: "#B45309", border: "#FDE68A", bg: "#FFFBEB" },
  High:   { text: "#9F1239", border: "#FECDD3", bg: "#FFF1F2" },
};

const STATUS_TABS  = ["All", "Active", "At Risk", "Exchanged", "Completed", "Fallen Through"];
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

// ─── Utilities ───────────────────────────────────────────────────

const initials = (name) => {
  if (!name) return "?";
  return name.split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();
};

const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(String(value).replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return typeof value === "string" ? value : "—";
  if (n >= 1_000_000) return `£${n.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
  return `£${n.toLocaleString("en-GB")}`;
};

const relativeFromNow = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60_000);
  if (mins < 60)        return "today";
  const hrs  = Math.floor(mins / 60);
  if (hrs < 24)         return "today";
  const days = Math.floor(hrs / 24);
  if (days === 1)       return "yesterday";
  if (days < 7)         return `${days} days ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

// ─── Page ─────────────────────────────────────────────────────────

const VIEW_TABS = [
  { id: "transactions", label: "Transactions" },
  { id: "properties",   label: "Properties"   },
  { id: "progression",  label: "Progression"  },
];

export default function DealsPage() {
  const { deals, findProperty, findContact, stages } = useChainStore();
  const { isDemoDeal } = useSampleData();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialView = (() => {
    const v = searchParams.get("tab");
    return VIEW_TABS.some((t) => t.id === v) ? v : "transactions";
  })();
  const [view, setView] = useState(initialView);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (view === "transactions") next.delete("tab");
    else next.set("tab", view);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const v = searchParams.get("tab");
    const target = VIEW_TABS.some((t) => t.id === v) ? v : "transactions";
    if (target !== view) setView(target);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const [query, setQuery]       = useState("");
  const [status, setStatus]     = useState("All");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage]         = useState(1);

  const stageLabel = (id) => stages.find((s) => s.id === id)?.label || id;

  const kpi = useMemo(() => {
    const c = { Active: 0, "At Risk": 0, Exchanged: 0, Completed: 0, "Fallen Through": 0 };
    for (const d of deals) c[d.status] = (c[d.status] || 0) + 1;
    return c;
  }, [deals]);

  const filtered = useMemo(() => {
    return deals.filter((d) => {
      if (status !== "All" && d.status !== status) return false;
      if (!query) return true;
      const prop   = findProperty(d.propertyId);
      const buyer  = findContact(d.buyerId);
      const seller = findContact(d.sellerId);
      const t = query.toLowerCase();
      return (
        (prop?.address || "").toLowerCase().includes(t) ||
        (buyer?.name   || "").toLowerCase().includes(t) ||
        (seller?.name  || "").toLowerCase().includes(t) ||
        (d.id          || "").toLowerCase().includes(t) ||
        stageLabel(d.stage).toLowerCase().includes(t)
      );
    });
  }, [deals, status, query, findProperty, findContact, stages]); // eslint-disable-line react-hooks/exhaustive-deps

  const total     = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage  = Math.min(page, pageCount);
  const visible   = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const isMobileProgression = view === "progression";

  return (
    <div className="min-h-screen" style={{ background: "#F8F8FB" }}>
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex flex-col min-h-screen overflow-x-hidden" data-testid="deals-main">
        <div className={`max-w-[1480px] mx-auto w-full ${isMobileProgression ? "md:px-10 md:py-10 md:space-y-7" : "px-4 md:px-10 py-8 md:py-10 space-y-7"}`}>
          <div className={isMobileProgression ? "hidden md:block" : ""}>
            <Header
              query={query} setQuery={setQuery}
              onCreate={() => openAddProperty()}
            />
          </div>
          <div className={isMobileProgression ? "hidden md:block" : ""}>
            <ViewTabs view={view} setView={setView} />
          </div>
          {view === "transactions" && (
            <>
              <KpiStrip counts={kpi} />
              <DealsChainOverview deals={deals} />
              <FilterRow
                status={status} setStatus={(s) => { setStatus(s); setPage(1); }}
              />
              <TransactionTable
                rows={visible}
                findProperty={findProperty}
                findContact={findContact}
                stageLabel={stageLabel}
                stages={stages}
                isDemoDeal={isDemoDeal}
                nav={nav}
              />
              <Pagination
                page={safePage} pageCount={pageCount}
                pageSize={pageSize} setPageSize={(s) => { setPageSize(s); setPage(1); }}
                setPage={setPage} total={total}
              />
            </>
          )}
          {view === "properties"  && <PropertiesPage embedded />}
          {view === "viewings"    && <ViewingsPage embedded />}
          {view === "progression" && <SalesProgressionPage embedded />}
        </div>
      </main>
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────────

function DealsChainOverview({ deals }) {
  const anchor = pickAnchorDeal(deals || []);
  if (!anchor) return null;
  return (
    <ChainRibbon
      deal={anchor}
      allDeals={deals}
      title="Most Urgent Chain"
      testid="deals-chain-ribbon"
    />
  );
}

function Header({ query, setQuery, onCreate }) {
  return (
    <header
      className="flex items-start lg:items-end justify-between gap-4 flex-col lg:flex-row pl-12 md:pl-0"
      data-testid="deals-header"
    >
      <div className="min-w-0">
        <h1
          className="text-[32px] md:text-[40px] font-bold tracking-[-0.025em] text-slate-900 leading-[1.05]"
          data-testid="deals-title"
        >
          Transactions
        </h1>
        <p
          className="text-[13.5px] text-slate-500 mt-1.5"
          data-testid="deals-subtitle"
        >
          Every active property transaction in one operational workspace.
        </p>
      </div>

      <div className="flex items-center gap-2.5 self-stretch lg:self-auto shrink-0 w-full lg:w-auto">
        <div
          className="relative flex items-center h-10 px-3 rounded-xl bg-white border border-slate-100 flex-1 lg:w-[340px]"
          style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
        >
          <Search size={13} className="text-slate-400 shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by property, party, stage or deal ID…"
            className="bg-transparent w-full pl-2 text-[12.5px] outline-none placeholder:text-slate-400 text-slate-700"
            data-testid="deals-search-input"
          />
        </div>
        <button
          type="button"
          className="h-10 px-3.5 rounded-xl bg-white border border-slate-100 inline-flex items-center gap-2 text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-200 transition-colors shrink-0"
          aria-label="Filters"
          data-testid="deals-filter-button"
          style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
        >
          <SlidersHorizontal size={13} /> Filters
        </button>
        <button
          type="button"
          onClick={onCreate}
          className="h-10 px-4 rounded-xl text-[12.5px] font-semibold text-white inline-flex items-center gap-1.5 shrink-0 hover:opacity-95 cf-press transition-opacity"
          style={{
            background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
            boxShadow:  "0 8px 20px -10px rgba(15,23,42,0.40)",
          }}
          data-testid="deals-create-button"
        >
          <Plus size={13} strokeWidth={2.5} /> Add Property
        </button>
      </div>
    </header>
  );
}

// ─── View tabs (underline style) ──────────────────────────────────

function ViewTabs({ view, setView }) {
  return (
    <nav
      className="flex items-center gap-7 border-b border-slate-100 overflow-x-auto"
      data-testid="deals-view-tabs"
    >
      {VIEW_TABS.map((t) => {
        const isActive = view === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => setView(t.id)}
            className={`relative h-10 text-[13px] font-semibold whitespace-nowrap transition-colors ${
              isActive ? "text-violet-700" : "text-slate-500 hover:text-slate-900"
            }`}
            data-testid={`deals-view-tab-${t.id}`}
          >
            {t.label}
            {isActive && (
              <span
                className="absolute left-0 right-0 -bottom-px h-[2px] rounded-full"
                style={{ background: "#7C3AED" }}
              />
            )}
          </button>
        );
      })}
    </nav>
  );
}

// ─── KPI strip ────────────────────────────────────────────────────

const KPI_DEFS = [
  { key: "Active",         label: "Active deals",   Icon: TrendingUp,    iconBg: "#EDE9FE", iconColor: "#6D28D9", trend: "12% vs last 30 days", trendUp: true  },
  { key: "At Risk",        label: "At risk",        Icon: AlertTriangle, iconBg: "#FFFBEB", iconColor: "#B45309", trend: "4% vs last 30 days",  trendUp: false },
  { key: "Exchanged",      label: "Exchanged",      Icon: ShieldCheck,   iconBg: "#F5F3FF", iconColor: "#6D28D9", trend: "8% vs last 30 days",  trendUp: true  },
  { key: "Completed",      label: "Completed",      Icon: CheckCircle2,  iconBg: "#ECFDF5", iconColor: "#047857", trend: "3% vs last 30 days",  trendUp: true  },
  { key: "Fallen Through", label: "Fallen through", Icon: XCircle,       iconBg: "#FFF1F2", iconColor: "#9F1239", trend: "Holding steady",      trendUp: null  },
];

function KpiStrip({ counts }) {
  return (
    <section
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5"
      data-testid="deals-kpi-strip"
    >
      {KPI_DEFS.map((k) => {
        const Icon = k.Icon;
        const value = counts?.[k.key] ?? 0;
        const TrendIcon = k.trendUp === true ? ArrowUp : k.trendUp === false ? ArrowDown : null;
        const trendColor =
          k.trendUp === true ? "#047857"
          : k.trendUp === false ? "#B45309"
          : "#94A3B8";
        return (
          <article
            key={k.key}
            className="bg-white rounded-2xl p-5 border border-slate-100 cf-card-hover"
            style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
            data-testid={`deals-kpi-${k.key.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span
              className="inline-flex w-9 h-9 rounded-xl items-center justify-center"
              style={{ background: k.iconBg }}
            >
              <Icon size={14} style={{ color: k.iconColor }} strokeWidth={2.2} />
            </span>
            <p className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-slate-500 mt-4">
              {k.label}
            </p>
            <p
              className="text-[28px] md:text-[32px] font-semibold tabular-nums text-slate-900 leading-none mt-1.5"
              data-testid={`deals-kpi-${k.key.toLowerCase().replace(/\s+/g, "-")}-value`}
            >
              {value}
            </p>
            <p
              className="text-[11px] mt-3 inline-flex items-center gap-1 font-medium"
              style={{ color: trendColor }}
            >
              {TrendIcon ? <TrendIcon size={11} strokeWidth={2.4} /> : null}
              {k.trend}
            </p>
          </article>
        );
      })}
    </section>
  );
}

// ─── Filter row ───────────────────────────────────────────────────

function FilterRow({ status, setStatus }) {
  return (
    <section
      className="flex items-center gap-3 flex-wrap justify-between"
      data-testid="deals-filter-row"
    >
      <div
        className="flex items-center gap-1 overflow-x-auto"
        data-testid="deals-status-tabs"
      >
        {STATUS_TABS.map((t) => {
          const isActive = t === status;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setStatus(t)}
              className={`h-9 px-3.5 rounded-lg text-[12.5px] font-semibold whitespace-nowrap transition-colors ${
                isActive ? "text-white" : "text-slate-500 hover:text-slate-900"
              }`}
              style={
                isActive
                  ? { background: "#0F172A", boxShadow: "0 4px 10px -6px rgba(15,23,42,0.40)" }
                  : undefined
              }
              data-testid={`deals-status-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {t}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className="h-9 w-9 rounded-lg bg-white border border-slate-100 inline-flex items-center justify-center text-slate-500 hover:text-slate-900 hover:border-slate-200 shrink-0"
        aria-label="Configure columns"
        data-testid="deals-columns-button"
        style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
      >
        <SlidersHorizontal size={13} />
      </button>
    </section>
  );
}

// ─── Table ────────────────────────────────────────────────────────

function TransactionTable({ rows, findProperty, findContact, stageLabel, stages, isDemoDeal, nav }) {
  return (
    <section
      className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
      data-testid="deals-table"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left" data-testid="deals-table-element">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 bg-slate-50/40 border-b border-slate-100">
              <th className="px-6 py-3 font-bold">Property</th>
              <th className="px-6 py-3 font-bold">Parties</th>
              <th className="px-6 py-3 font-bold">Stage</th>
              <th className="px-6 py-3 font-bold">Status</th>
              <th className="px-6 py-3 font-bold">Risk</th>
              <th className="px-6 py-3 font-bold">Progress</th>
              <th className="px-6 py-3 font-bold text-right">Value</th>
              <th className="px-4 py-3 font-bold w-[1%]" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-16 text-center text-[13px] text-slate-500">
                  No transactions match your filters.
                </td>
              </tr>
            ) : (
              rows.map((d) => (
                <TransactionRow
                  key={d.id}
                  deal={d}
                  property={findProperty(d.propertyId)}
                  buyer={findContact(d.buyerId)}
                  seller={findContact(d.sellerId)}
                  stageLabel={stageLabel(d.stage)}
                  stages={stages}
                  isDemo={isDemoDeal?.(d) || false}
                  onOpen={() => nav(`/deal-rooms/${d.id}`)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TransactionRow({ deal, property, buyer, seller, stageLabel, stages, isDemo, onOpen }) {
  const status     = deal.status;
  const risk       = deal.risk;
  const stageIdx   = Math.max(0, stages.findIndex((s) => s.id === deal.stage));
  const stageTotal = stages.length;
  const stageDone  = stageIdx + 1;
  const progress   = Math.round((stageDone / stageTotal) * 100);
  const sTone      = STATUS_TONE[status] || STATUS_TONE.Active;
  const rTone      = RISK_TONE[risk]     || RISK_TONE.Medium;
  const updated    = relativeFromNow(deal.updatedAt || deal.updated_at || deal.createdAt || deal.created_at);

  return (
    <tr
      className="border-b last:border-b-0 border-slate-100 hover:bg-slate-50/50 cursor-pointer transition-colors"
      onClick={onOpen}
      data-testid={`deals-row-${deal.id}`}
    >
      {/* Property */}
      <td className="px-6 py-5">
        <div className="flex items-center gap-3 min-w-0">
          <Thumb image={property?.thumb || property?.image} address={property?.address || deal.name} />
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-slate-900 leading-snug truncate">
              {property?.address || deal.name || "Untitled property"}
            </p>
            <p className="text-[11.5px] text-slate-500 truncate mt-0.5">
              {property?.type || "Residential"}
            </p>
          </div>
          {isDemo ? (
            <span
              className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.14em]"
              style={{ background: "#F5F3FF", color: "#5B21B6", border: "1px solid #DDD6FE" }}
            >
              Sample
            </span>
          ) : null}
        </div>
      </td>

      {/* Parties */}
      <td className="px-6 py-5">
        <div className="space-y-1.5">
          <PartyLine name={buyer?.name}  role="Buyer"  tint="#6D28D9" />
          <PartyLine name={seller?.name} role="Seller" tint="#0E7490" />
        </div>
      </td>

      {/* Stage */}
      <td className="px-6 py-5 min-w-[160px]">
        <p className="text-[12.5px] font-semibold text-slate-900 leading-tight">{stageLabel}</p>
        <p className="text-[11px] text-slate-400 mt-1">{deal.role || "Buyer"}</p>
      </td>

      {/* Status */}
      <td className="px-6 py-5">
        <span
          className="inline-flex items-center px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.14em] bg-white"
          style={{ color: sTone.text, border: `1px solid ${sTone.border}` }}
          data-testid={`deals-row-status-${deal.id}`}
        >
          {status}
        </span>
        {updated && (
          <p className="text-[10.5px] text-slate-400 mt-1.5">Updated {updated}</p>
        )}
      </td>

      {/* Risk */}
      <td className="px-6 py-5">
        <span
          className="inline-flex items-center px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.14em] bg-white"
          style={{ color: rTone.text, border: `1px solid ${rTone.border}` }}
          data-testid={`deals-row-risk-${deal.id}`}
        >
          {risk}
        </span>
      </td>

      {/* Progress */}
      <td className="px-6 py-5 min-w-[140px]">
        <p className="text-[11.5px] text-slate-500 tabular-nums mb-1.5">
          <span className="font-semibold text-slate-700">{stageDone}</span> / {stageTotal}
        </p>
        <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${progress}%`,
              background: "linear-gradient(90deg, #8B5CF6 0%, #6366F1 100%)",
            }}
          />
        </div>
        <p className="text-[10.5px] text-slate-400 mt-1 tabular-nums">{progress}%</p>
      </td>

      {/* Value */}
      <td className="px-6 py-5 text-right">
        <span
          className="text-[14px] font-semibold text-slate-900 tabular-nums whitespace-nowrap"
          data-testid={`deals-row-price-${deal.id}`}
        >
          {/* iter-alignment · P0 · Price SSOT Audit (2026-02-28) —
              Deals table "Value" column is a transactional
              surface → read deal.agreed_price first, then
              legacy deal.value; NEVER cross into
              property.price (Asking Price) which was
              masquerading as the Value.  Fee is a separate
              revenue column and belongs in its own cell, not
              this one. */}
          {formatPrice(deal.agreedPrice ?? deal.value ?? deal.fee)}
        </span>
      </td>

      {/* Open / overflow */}
      <td className="px-4 py-5 text-right">
        <Link
          to={`/deal-rooms/${deal.id}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          aria-label="Open transaction"
          data-testid={`deals-row-open-${deal.id}`}
        >
          <MoreHorizontal size={14} />
        </Link>
      </td>
    </tr>
  );
}

function Thumb({ image, address }) {
  if (image) {
    return (
      <span
        className="w-12 h-12 rounded-xl overflow-hidden shrink-0"
        style={{ border: "1px solid #E2E8F0" }}
      >
        <img src={image} alt={address || ""} className="w-full h-full object-cover" />
      </span>
    );
  }
  return (
    <span
      className="w-12 h-12 rounded-xl shrink-0 inline-flex items-center justify-center text-[11px] font-bold tracking-[0.14em] text-violet-700"
      style={{
        background: "linear-gradient(135deg, rgba(139,92,246,0.10), rgba(99,102,241,0.04))",
        border:     "1px solid rgba(139,92,246,0.18)",
      }}
    >
      CF
    </span>
  );
}

function PartyLine({ name, role, tint }) {
  const known = !!name;
  return (
    <div className="flex items-center gap-2 min-w-0" data-testid={`deals-party-${(role || "").toLowerCase()}`}>
      <span
        className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[9.5px] font-bold shrink-0"
        style={{
          background: tint ? `${tint}14` : "#F1F5F9",
          color:      tint || "#475569",
          border:     tint ? `1px solid ${tint}28` : "1px solid #E2E8F0",
          opacity:    known ? 1 : 0.65,
        }}
      >
        {known ? initials(name) : role[0]}
      </span>
      <span className={`text-[12px] truncate ${known ? "text-slate-700 font-medium" : "text-slate-400"}`}>
        {name || `${role} · pending`}
      </span>
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────

function Pagination({ page, pageCount, pageSize, setPageSize, setPage, total }) {
  return (
    <footer
      className="flex items-center justify-between gap-3 flex-col sm:flex-row text-[12px] text-slate-500 pt-1"
      data-testid="deals-pagination"
    >
      <span>
        Showing{" "}
        <span className="font-semibold text-slate-700 tabular-nums">
          {Math.min(total, (page - 1) * pageSize + 1)}–{Math.min(total, page * pageSize)}
        </span>{" "}
        of{" "}
        <span className="font-semibold text-slate-700 tabular-nums">{total}</span>{" "}
        transactions
      </span>
      <div className="flex items-center gap-2.5">
        <label className="flex items-center gap-1.5">
          <span className="text-slate-400">Per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="h-8 px-2 rounded-md border border-slate-100 bg-white text-[12px] font-semibold text-slate-700"
            data-testid="deals-page-size"
          >
            {PAGE_SIZE_OPTIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="h-8 w-8 rounded-md border border-slate-100 bg-white inline-flex items-center justify-center text-slate-500 hover:text-slate-900 disabled:opacity-40 transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft size={13} />
          </button>
          <span
            className="h-8 min-w-[32px] px-2 rounded-md inline-flex items-center justify-center text-[12px] font-semibold tabular-nums text-white"
            style={{ background: "#0F172A" }}
          >
            {page}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={page >= pageCount}
            className="h-8 w-8 rounded-md border border-slate-100 bg-white inline-flex items-center justify-center text-slate-500 hover:text-slate-900 disabled:opacity-40 transition-colors"
            aria-label="Next page"
          >
            <ChevronRight size={13} />
          </button>
        </div>
      </div>
    </footer>
  );
}
